import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Globe, Loader2, Search, Sparkles, XCircle } from 'lucide-react';

type DomainResult = {
  domain: string;
  available: boolean;
  note: string;
};

const extensions = ['.com', '.co.zm', '.africa', '.org', '.net'];
const reservedDomains = new Set([
  'findura.com',
  'findura.co.zm',
  'google.com',
  'facebook.com',
  'microsoft.com',
  'apple.com',
  'amazon.com',
]);

function normalizeDomainInput(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');
}

function getAvailability(domain: string) {
  if (reservedDomains.has(domain)) {
    return false;
  }

  const score = domain.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return score % 4 !== 0;
}

function buildResults(term: string): DomainResult[] {
  return extensions.map((extension) => {
    const domain = `${term}${extension}`;
    const available = getAvailability(domain);

    return {
      domain,
      available,
      note: available ? 'Ready for registration' : 'Already registered or reserved',
    };
  });
}

function buildSuggestions(term: string) {
  return [`get${term}`, `${term}online`, `${term}hq`]
    .map(normalizeDomainInput)
    .filter((item, index, all) => item && all.indexOf(item) === index)
    .slice(0, 3);
}

export default function DomainChecker() {
  const [query, setQuery] = useState('');
  const [checking, setChecking] = useState(false);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [results, setResults] = useState<DomainResult[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    const normalized = normalizeDomainInput(query);

    if (!normalized || normalized.length < 3) {
      setError('Enter at least 3 characters to check a domain name.');
      setResults([]);
      setSuggestions([]);
      setSearchedTerm('');
      return;
    }

    setError('');
    setChecking(true);
    setSearchedTerm(normalized);

    await new Promise((resolve) => setTimeout(resolve, 900));

    const nextResults = buildResults(normalized);
    const availableCount = nextResults.filter((result) => result.available).length;

    setResults(nextResults);
    setSuggestions(availableCount > 0 ? [] : buildSuggestions(normalized));
    setChecking(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '1rem 1rem 1rem 3rem',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.8)',
    border: '1px solid var(--gray-light)',
    color: 'var(--text)',
    fontSize: '1rem',
    outline: 'none',
    fontFamily: 'DM Sans',
  };

  return (
    <section id="domain-checker" style={{ padding: '8rem 2rem', position: 'relative' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <span style={{
            display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--text)', marginBottom: '1rem',
            padding: '0.3rem 0.9rem', borderRadius: 100,
            background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
          }}>Domain Search</span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', letterSpacing: '-0.02em', color: 'var(--text)' }}>
            Check Your Next <span style={{ color: 'var(--orange)' }}>Domain Name</span>
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-soft)', maxWidth: 620, margin: '0 auto' }}>
            Test a brand name, compare common extensions, and identify the strongest options before you register.
          </p>
        </motion.div>

        <div className="domain-checker-grid" style={{ display: 'grid', gridTemplateColumns: '0.95fr 1.25fr', gap: '2rem', alignItems: 'stretch' }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              padding: '2rem',
              background: 'linear-gradient(160deg, var(--navy), var(--navy-dark))',
              color: 'white',
              borderRadius: 24,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top right, rgba(255,107,0,0.3), transparent 35%)',
              pointerEvents: 'none',
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 54, height: 54, borderRadius: 16, marginBottom: '1.25rem',
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Globe size={24} color="var(--orange)" />
              </div>
              <h3 style={{ fontFamily: 'Syne', fontSize: '1.5rem', marginBottom: '0.8rem' }}>
                Domain planning built into your website
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: '1.75rem' }}>
                Give prospects a fast way to explore names for new websites, campaigns, products, and business launches.
              </p>

              <div style={{ display: 'grid', gap: '0.85rem', marginBottom: '1.75rem' }}>
                {[
                  'Compare local and global domain extensions',
                  'Spot available options before contacting your team',
                  'Create stronger naming alternatives when the first choice is taken',
                ].map((item) => (
                  <div key={item} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <Sparkles size={16} color="var(--orange)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.88rem', lineHeight: 1.6 }}>{item}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {extensions.map((extension) => (
                  <span
                    key={extension}
                    style={{
                      padding: '0.45rem 0.8rem',
                      borderRadius: 999,
                      fontSize: '0.78rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.16)',
                      color: 'rgba(255,255,255,0.88)',
                    }}
                  >
                    {extension}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              padding: '2rem',
              background: 'rgba(255,255,255,0.72)',
              border: '1px solid #3B82F6',
              borderRadius: 24,
              backdropFilter: 'blur(24px)',
            }}
          >
            <div className="domain-checker-search-row" style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={18} color="var(--text-soft)" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  style={inputStyle}
                  placeholder="Enter a name like findura, lusakastartup, or zamtech"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCheck();
                    }
                  }}
                  onFocus={(e) => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                  onBlur={(e) => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }}
                />
              </div>
              <button
                onClick={handleCheck}
                disabled={checking}
                style={{
                  padding: '1rem 1.35rem',
                  borderRadius: 14,
                  background: 'var(--orange)',
                  color: 'var(--white)',
                  fontFamily: 'Syne',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  minWidth: 150,
                  boxShadow: '0 18px 38px var(--electric-glow)',
                  opacity: checking ? 0.8 : 1,
                }}
              >
                {checking ? (
                  <span style={{ display: 'inline-flex', gap: '0.45rem', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Checking
                  </span>
                ) : 'Check Now'}
              </button>
            </div>

            <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '1.5rem' }}>
              Search across `.com`, `.co.zm`, `.africa`, `.org`, and `.net`.
            </p>

            {error ? (
              <div style={{
                padding: '0.9rem 1rem',
                borderRadius: 14,
                background: 'rgba(255,107,0,0.08)',
                border: '1px solid rgba(255,107,0,0.18)',
                color: 'var(--orange)',
                fontSize: '0.88rem',
                marginBottom: '1rem',
              }}>
                {error}
              </div>
            ) : null}

            {results.length > 0 ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Search term</div>
                  <div style={{ fontFamily: 'Syne', fontSize: '1.2rem', color: 'var(--text)' }}>{searchedTerm}</div>
                </div>

                <div style={{ display: 'grid', gap: '0.85rem' }}>
                  {results.map((result) => (
                    <div
                      className="domain-checker-result-row"
                      key={result.domain}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        padding: '1rem 1.1rem',
                        borderRadius: 16,
                        background: result.available ? 'rgba(39, 174, 96, 0.08)' : 'rgba(26, 36, 86, 0.04)',
                        border: result.available ? '1px solid rgba(39, 174, 96, 0.2)' : '1px solid var(--gray-light)',
                      }}
                    >
                      <div>
                        <div style={{ fontFamily: 'Syne', fontSize: '1rem', color: 'var(--text)', marginBottom: '0.25rem' }}>
                          {result.domain}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>{result.note}</div>
                      </div>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        color: result.available ? '#1B8A4B' : 'var(--text-soft)',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        whiteSpace: 'nowrap',
                      }}>
                        {result.available ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                        {result.available ? 'Available' : 'Taken'}
                      </div>
                    </div>
                  ))}
                </div>

                {suggestions.length > 0 ? (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.75rem' }}>
                      No strong matches found. Try these alternatives:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                      {suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => {
                            setQuery(suggestion);
                            setError('');
                          }}
                          style={{
                            padding: '0.75rem 0.95rem',
                            borderRadius: 12,
                            border: '1px solid var(--gray-light)',
                            background: 'white',
                            color: 'var(--text)',
                            fontSize: '0.85rem',
                          }}
                        >
                          {suggestion}.com
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{
                minHeight: 260,
                borderRadius: 20,
                border: '1px dashed var(--gray-light)',
                background: 'linear-gradient(180deg, rgba(255,107,0,0.03), rgba(255,255,255,0.7))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '2rem',
              }}>
                <div>
                  <Globe size={32} color="var(--orange)" style={{ margin: '0 auto 0.9rem' }} />
                  <h3 style={{ fontFamily: 'Syne', fontSize: '1.2rem', color: 'var(--text)', marginBottom: '0.5rem' }}>
                    Start with your brand name
                  </h3>
                  <p style={{ color: 'var(--text-soft)', fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 420 }}>
                    Enter a business, product, or campaign name to preview likely domain options and identify stronger alternatives.
                  </p>
                </div>
              </div>
            )}

            <div style={{ marginTop: '1.5rem', fontSize: '0.78rem', color: 'var(--text-soft)', lineHeight: 1.6 }}>
              Need live registrar verification and direct registration flows? Findura can connect this interface to your preferred domain provider.
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 900px) {
          .domain-checker-grid { grid-template-columns: 1fr !important; }
        }

        @media (max-width: 640px) {
          #domain-checker button[disabled],
          #domain-checker button:not([disabled]) {
            min-width: 0 !important;
          }

          .domain-checker-search-row,
          .domain-checker-result-row {
            flex-direction: column;
            align-items: stretch !important;
          }

          .domain-checker-result-row {
            gap: 0.75rem !important;
          }
        }
      `}</style>
    </section>
  );
}
